import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useRef } from "react";
import { toast } from "sonner";
import { TenantSwitcherContext } from "@/features/tenant-switcher/tenant-switcher-context";
import { apiFetch, apiFetchBlob } from "@/lib/auth-api";
import { playNewTicketSound, useNewTicketSound } from "./new-ticket-sound";

/**
 * Kontrakt API helpdesku: `doc/helpdesk/README.md`. Ścieżki bez `/api`
 * (dokłada `apiFetch`), odpowiedzi camelCase. Wszystko wyłącznie dla super_admina.
 */

export type Category = "apteki" | "robaczek" | "alerty" | "pomysly";
export type TicketType = "zgloszenie" | "zlecenie";
export type TicketStatus = "open" | "closed";
/** Status w skrzynce: `new` = otwarte, przy którym pracownik jeszcze nic nie zrobił. */
export type InboxStatus = TicketStatus | "new";
export const STATUS_LABEL: Record<InboxStatus, string> = {
	new: "Nowe",
	open: "Otwarte",
	closed: "Zakończone",
};
/** `mine` — otwarte przypisane do zalogowanego; `all` bez przycisku (widok Spam, link z wyszukiwarki). */
export type StatusFilter = TicketStatus | "all" | "mine";

export const CATEGORY_LABEL: Record<Category, string> = {
	apteki: "Apteki",
	robaczek: "Robaczek",
	alerty: "Alerty",
	pomysly: "Pomysły",
};
export const CATEGORIES = Object.keys(CATEGORY_LABEL) as Category[];

export const TICKET_TYPE_LABEL: Record<TicketType, string> = {
	zgloszenie: "Zgłoszenie",
	zlecenie: "Zlecenie",
};

/** Założenie: kontrakt nie definiuje `PharmacyName` — przyjmujemy `{ idfirm, name }`. */
export interface PharmacyName {
	idfirm: number;
	name: string;
}

export interface Ticket {
	id: number;
	number: number;
	idfirm: number | null;
	contactEmail: string | null;
	contactName: string | null;
	subject: string;
	category: Category;
	ticketType: TicketType;
	status: TicketStatus;
	source: "email" | "otrs" | "robaczek" | "manual" | "stanowisko";
	/** Zgłoszenie z ikony agenta: stanowisko, test Canary, kategoria, prośba o telefon. */
	deviceUuid: string | null;
	isTest: boolean;
	stationTopic: string | null;
	callbackPhone: string | null;
	openedAt: string;
	closedAt: string | null;
	closedBy: number | null;
	closedByName: string | null;
	reopenedAt: string | null;
	reopenCount: number;
	previousTicketId: number | null;
	lastActivityAt: string;
	lastCustomerAt: string | null;
	articleCount: number;
	summary: string | null;
	aiClassification: unknown;
	githubIssueNumber: number | null;
	otrsTn: string | null;
	/** Nadawca niezweryfikowany (DMARC/SPF) — bez „przyjęte” i automatycznej diagnozy. */
	suspicious: string | null;
	/** W kwarantannie (widok Spam). */
	spamAt: string | null;
	spamReason: string | null;
	spamRuleId: number | null;
	/** Osoba przypisana (super_admin); `null` = nieprzypisane. */
	assigneeUserId: number | null;
	/** Alert z reguły „tylko w historii apteki”: zamknięty, poza skrzynką, w czacie jedna linia. */
	historyAt: string | null;
	/** Awaria zbiorcza, do której należy zgłoszenie (makieta). */
	incidentId: number | null;
}

export interface Incident {
	id: number;
	title: string;
	status: "open" | "resolved";
	createdByName: string | null;
	createdAt: string;
	resolvedAt: string | null;
	resolvedByName: string | null;
	/** Ostatnia odpowiedź awarii — dostaje ją zgłoszenie dołączone później. */
	lastReplyAt: string | null;
}

/** Otwarte zgłoszenie apteki podobne do zgłoszeń trwającej awarii, jeszcze do niej niedołączone. */
export interface IncidentCandidate {
	incidentId: number;
	number: number;
	subject: string;
	pharmacyName: string | null;
	contactEmail: string | null;
}

export interface IncidentTicket {
	number: number;
	subject: string;
	status: TicketStatus;
	idfirm: number | null;
	pharmacyName: string | null;
	contactEmail: string | null;
	openedAt: string;
	/** Zakończone przy dołączeniu — dostanie komunikat o zakończeniu awarii. */
	incidentWait: boolean;
	lastReplyAt: string | null;
	lastReplyStatus: DeliveryStatus | null;
}

/** Podgląd „Możliwa awaria” (≥3 apteki w 60 min z podobną treścią). */
export interface IncidentSuggestion {
	numbers: number[];
	pharmacies: number;
	since: string;
	label: string;
}

export type ArticleOrigin = "human" | "auto" | "ai_draft" | "ai_diagnosis" | "import";
export type DeliveryStatus = "queued" | "sending" | "sent" | "failed" | "cancelled";

export interface Article {
	id: number;
	ticketId: number;
	sender: "customer" | "agent" | "system";
	/** `web` — formularz Robaczka. */
	kind: "email" | "note" | "phone" | "web" | "event";
	internal: boolean;
	origin: ArticleOrigin;
	event:
		| "opened"
		| "closed"
		| "reopened"
		| "paired"
		| "category"
		| "edited"
		| "linked"
		| "spam"
		/** Pracownik zmienił zgłaszającego. */
		| "contact"
		| null;
	fromAddr: string | null;
	fromName: string | null;
	toAddrs: string[];
	ccAddrs: string[];
	subject: string | null;
	messageId: string | null;
	bodyText: string;
	bodyHtml: string | null;
	autoSubmitted: boolean;
	authorUserId: number | null;
	authorName: string | null;
	createdAt: string;
	deliveryStatus: DeliveryStatus | null;
	deliveryError: string | null;
	sentAt: string | null;
	aiMeta: unknown;
}

export interface Attachment {
	id: number;
	articleId: number;
	filename: string;
	contentType: string;
	sizeBytes: number;
	contentId: string | null;
	isInline: boolean;
}

/** `GET /helpdesk/attachments/{id}/preview`: tekst dokumentu i liczba pominiętych obrazków. */
export interface DocPreview {
	format: "odt" | "docx";
	text: string;
	truncated: boolean;
	images: { total: number; emf: number; wmf: number; svg: number };
}

export interface InboxItem {
	number: number;
	subject: string;
	category: Category;
	ticketType: TicketType;
	status: InboxStatus;
	idfirm: number | null;
	pharmacyName: string | null;
	contactEmail: string | null;
	contactName: string | null;
	openedAt: string;
	/** Pierwsze działanie pracownika; `null` = „Nowe” (licznik reakcji biegnie). */
	firstTouchAt: string | null;
	/** Pierwsze otwarcie przez pracownika; `null` przy „Nowe” = zielony wiersz. */
	seenAt: string | null;
	/** Otwarte z odpowiedzią apteki, której nikt z zespołu jeszcze nie otworzył — zielony wiersz „Odpowiedź”. */
	hasReply: boolean;
	closedAt: string | null;
	lastActivityAt: string;
	lastCustomerAt: string | null;
	articleCount: number;
	/** Ostatnia wiadomość bez automatów (potwierdzenia przyjęcia), 240 znaków. */
	preview: string | null;
	/** Ostatnia wiadomość apteki (do 2000 znaków) — podpowiedź nad podglądem. */
	customerMessage: string | null;
	summary: string | null;
	/** Bez apteki i bez etykiety adresu („Do sparowania”). */
	unpaired: boolean;
	/** Etykieta adresu spoza aptek („Biuro”) — tylko przy zgłoszeniu bez apteki. */
	label: string | null;
	/** Najnowsza notatka pracownika (ikonka w wierszu). */
	note: StaffNote | null;
	/** Kto z zespołu ostatnio pisał (odpowiedź, telefon albo notatka); `note` = tylko notatka wewnętrzna. */
	lastStaff: { author: string; at: string; note: boolean } | null;
	candidateIdfirms: number[];
	aiPending: boolean;
	classification: unknown;
	suspicious: string | null;
	spamAt: string | null;
	spamReason: string | null;
	source: Ticket["source"];
	isTest: boolean;
	stationTopic: string | null;
	callbackPhone: string | null;
	/** Osoba przypisana; nazwę daje lista `assignees`. */
	assigneeUserId: number | null;
}

/** Pracownik helpdesku do przypisania (aktywny super_admin). */
export interface Assignee {
	userId: number;
	name: string;
}

/** Notatka pracownika w skrzynce i nagłówku zgłoszenia: treść, autor, data. */
export interface StaffNote {
	body: string;
	author: string | null;
	at: string;
}

export interface InboxCounts {
	open: number;
	closed: number;
	unpaired: number;
	alertsOpen: number;
	byCategory: Record<Category, number>;
	spam: number;
	/** Otwarte przypisane do zalogowanego (bez alertów i spamu, jak `open`). */
	mine: number;
}

export interface InboxResponse {
	/** Kolejność serwera: Nowe, Otwarte, Zakończone, w grupie ostatnia aktywność malejąco. */
	items: InboxItem[];
	nextCursor: string | null;
	/** Liczby grup całego widoku (nagłówki „Nowe 16”); tylko na pierwszej stronie. */
	groups: Record<InboxStatus, number> | null;
	counts: InboxCounts;
}

export interface InboxFilters {
	status: StatusFilter;
	category: Category[];
	alerts: boolean;
	unpaired: boolean;
	/** Widok Spam (kwarantanna) zamiast skrzynki. */
	spam?: boolean;
	idfirm?: number;
	q?: string;
}

export type BlockRuleKind = "address" | "domain" | "phrase" | "subject";
/** Kwarantanna (widok Spam) albo „tylko w historii apteki” (alert automatyczny). */
export type BlockRuleAction = "quarantine" | "history";

export interface BlockRule {
	id: number;
	kind: BlockRuleKind;
	value: string;
	reason: string;
	action: BlockRuleAction;
	enabled: boolean;
	createdByName: string | null;
	createdAt: string;
	hitCount: number;
	lastHitAt: string | null;
}

export interface BlockRulePreview {
	kind: BlockRuleKind;
	/** Wartość po normalizacji (małe litery, domena bez `@`). */
	value: string;
	/** Istniejące wiadomości aptek, które reguła by złapała. */
	matches: number;
	/** Adres albo domena sparowane z apteką — ostrzeżenie przed blokadą. */
	paired: { email: string; idfirm: number; name: string | null }[];
}

export type ConversationTicket = Ticket & {
	articles?: Article[];
	attachments?: Attachment[];
	stationPopups?: StationPopup[];
};

export interface Conversation {
	pharmacy: PharmacyName | null;
	email: string | null;
	/** Etykieta adresu spoza aptek (rozmowa po adresie). */
	label: string | null;
	/** Imię i telefon z kontaktu adresu (rozmowa po adresie). */
	name: string | null;
	phone: string | null;
	/** Urządzenie biurowe adresu; `missing` = odwołane albo usunięte. */
	device: PanelDevice | { uuid: string; missing: true } | null;
	tickets: ConversationTicket[];
}

export interface TicketDetail {
	ticket: Ticket;
	articles: Article[];
	attachments: Attachment[];
	pharmacy: PharmacyName | null;
	candidates: PharmacyName[];
	/** Odpowiedzi pokazane okienkiem na stanowisku zgłoszenia. */
	stationPopups?: StationPopup[];
}

export interface StationPopup {
	articleId: number;
	sentAt: string;
	deliveredAt: string | null;
	readAt: string | null;
}

export interface Contact {
	email: string;
	name: string | null;
	phone: string | null;
	source: "mail" | "otrs" | "manual";
	/** Adres spoza aptek („Biuro”, „Dyrekcja”) — poza parowaniem. */
	label: string | null;
	/** Książka adresowa — podpowiedź w polach Do/DW. */
	inAddressBook: boolean;
	idfirms: number[];
	/** Urządzenie biurowe; `deviceName = null` przy `deviceUuid` = odwołane albo usunięte. */
	deviceUuid: string | null;
	deviceName: string | null;
}

export interface PanelDevice {
	uuid: string;
	displayName: string | null;
	hostname: string | null;
	deviceRole: string;
	status: string;
	lastSeenAt: string | null;
	serviceVersion: string | null;
}

export interface PharmacyPanel {
	pharmacy: PharmacyName & {
		email: string | null;
		region: string | null;
		street: string | null;
		zip: string | null;
		city: string | null;
		phone: string | null;
		wlasna: boolean;
		active: boolean;
	};
	contacts: Contact[];
	/** Kontakty z OTRS, których podpowiedź wskazuje tę aptekę — czekają na potwierdzenie człowieka. */
	suggestedContacts: {
		email: string;
		name: string | null;
		phone: string | null;
		reason: SuggestionReason;
		detail: string | null;
	}[];
	devices: PanelDevice[];
	openTickets: InboxItem[];
	counts: { open: number; closed: number };
	/** Adresy do powiadomień: kartoteka apteki (główny pierwszy) i sparowane kontakty. */
	addresses: string[];
}

export type SuggestionReason = "unitemail" | "otrs" | "ksid" | "alert" | "ai";

export const SUGGESTION_REASON_LABEL: Record<SuggestionReason, string> = {
	unitemail: "adres apteki",
	otrs: "konto OTRS",
	ksid: "KSID",
	alert: "alert",
	ai: "AI",
};

export interface PairingItem {
	email: string;
	name: string | null;
	phone: string | null;
	ticketCount: number;
	openCount: number;
	lastAt: string | null;
	sampleSubjects: string[];
	pairedIdfirms: number[];
	suggestions: { idfirm: number; name: string; reason: SuggestionReason; detail: string | null }[];
}

export type TemplateKey = "received" | "closed" | "closed_footer" | "signature";

export interface Template {
	key: TemplateKey;
	subject: string;
	body: string;
	enabled: boolean;
	updatedAt: string;
}

export interface StandardAttachment {
	id: number;
	name: string;
	filename: string;
	contentType: string;
	sizeBytes: number;
}

export interface Upload {
	id: number;
	filename: string;
	contentType: string;
	sizeBytes: number;
}

export type AiJobKind = "classify" | "draft" | "diagnose" | "summary" | "embed" | "memory";

export interface AiStatus {
	jobs: {
		kind: AiJobKind;
		status: "pending" | "running" | "done" | "failed" | "skipped";
		lastError: string | null;
		finishedAt: string | null;
	}[];
	classification: unknown;
}

export interface SearchHit {
	number: number;
	articleId: number | null;
	subject: string;
	idfirm: number | null;
	pharmacyName: string | null;
	category: Category;
	status: TicketStatus;
	createdAt: string;
	snippet: string;
	score: number;
	matchedBy: ("text" | "semantic")[];
}

export type RunKind =
	| "otrs_dry_run"
	| "otrs_import"
	| "hindsight_seed"
	| "qdrant_reindex"
	| "github_import"
	| "ai_catchup";

export interface Run {
	id: number;
	kind: RunKind;
	status: "pending" | "running" | "done" | "failed" | "cancelled";
	params: Record<string, unknown>;
	progress: Record<string, unknown>;
	report: unknown;
	error: string | null;
	createdAt: string;
	startedAt: string | null;
	finishedAt: string | null;
	/** Uruchomione przez system (nocne uzupełnienie, po imporcie), nie z panelu. */
	auto: boolean;
}

export interface MailboxSettings {
	enabled: boolean;
	imapHost: string;
	imapPort: number;
	imapSecurity: string;
	folder: string;
	smtpHost: string;
	smtpPort: number;
	smtpSecurity: string;
	username: string;
	password: string;
	fromAddress: string;
	fromName: string;
	pollSeconds: number;
	cleanupEnabled: boolean;
	retentionDays: number;
	/** Kwarantanna (widok Spam) sprzątana po tylu dniach. */
	spamDays: number;
}

/** Pola sekretów trzymają snake_case także po stronie API (`#[serde(rename)]` w settings.rs). */
export interface AiSettings {
	enabled: boolean;
	baseUrl: string;
	api_key: string;
	classifyModel: string;
	draftModel: string;
	diagnoseEnabled: boolean;
	diagnoseMaxCalls: number;
	diagnoseMaxSeconds: number;
	hindsightEnabled: boolean;
	hindsightUrl: string;
	hindsightBank: string;
	token: string;
}

export interface AlertRecipient {
	name: string;
	/** Po zapisie znormalizowany przez serwer (`48…`). */
	phone: string;
	enabled: boolean;
}

/** Alert SMS o krytycznych zgłoszeniach; godziny pełne `[od, do)` czasu warszawskiego. */
export interface AlertSettings {
	enabled: boolean;
	workdayFrom: number;
	workdayTo: number;
	holidayFrom: number;
	holidayTo: number;
	recipients: AlertRecipient[];
	phrases: string[];
	pharmacyHours: number;
	perHour: number;
	perDay: number;
}

export type AlertKind = "ticket" | "incident" | "exhausted" | "test";
export type AlertStatus = "sent" | "partial" | "failed";

export interface AlertLogItem {
	id: number;
	kind: AlertKind;
	ticketNumber: number | null;
	/** Zgłoszenia objęte SMS-em (awaria: cała grupa). */
	numbers: number[];
	idfirm: number | null;
	reason: string;
	body: string;
	recipients: {
		name: string;
		phone: string;
		status: "pending" | "sent" | "failed" | "skipped";
		error?: string | null;
	}[];
	status: AlertStatus;
	error: string | null;
	createdByName: string | null;
	createdAt: string;
}

export interface OtrsSettings {
	sshHost: string;
	sshPort: number;
	sshUser: string;
	private_key: string;
	passphrase: string;
	hostKeyFingerprint: string;
	years: number;
	pauseMs: number;
	batchSize: number;
}

/** Codzienny raport niezakończonych zgłoszeń przypisanych — wspólny dla organizacji. */
export interface ReportSettings {
	enabled: boolean;
	/** `HH:MM`, czas Warszawy, dni robocze. */
	time: string;
}

export type SettingsSection = "mailbox" | "ai" | "otrs" | "alerts" | "report";

/** Styl „Popraw z AI” (`REWRITE_STYLES` w `ai/prompts.rs`). */
export type RewriteStyle = "correct" | "professional" | "simple" | "friendly";

export interface MailboxStatus {
	enabled: boolean;
	/** Włączona skrzynka bez udanego odbioru od kilku minut. */
	stale: boolean;
	lastPollAt: string | null;
	lastOkAt: string | null;
	lastError: string | null;
	quota: { usedKb: number; limitKb: number; percent: number; warning: boolean } | null;
	outbox: { queued: number; failed: number };
}

/** Inny pracownik z otwartym zgłoszeniem (z sondy `version`, wygasa po ~15 s bez sondy). */
export interface Viewer {
	ticket: number;
	userId: number;
	name: string;
	/** Ma treść w oknie odpowiedzi albo notatki. */
	writing: boolean;
}

/** Administrator z otwartym Farmasterem (belka u góry każdej strony). */
export interface OnlineAdmin {
	userId: number;
	name: string;
	/** Widoczna karta i interakcja w ostatnich 5 min. */
	active: boolean;
	idleMin: number;
}

export interface MailboxTest {
	imap: { ok: boolean; error?: string; quota?: unknown };
	smtp: { ok: boolean; error?: string };
}

/** Niewysłana wiadomość: `queued`/`sending` (okno włączania skrzynki) albo `failed` (lista z „Wyślij ponownie”). */
export interface PendingMail {
	articleId: number;
	number: number;
	/** Szablon („przyjęte”, „zakończone”); `false` — odpowiedź agenta. */
	auto: boolean;
	toAddrs: string[];
	subject: string | null;
	createdAt: string;
	deliveryError: string | null;
	idfirm: number | null;
	contactEmail: string | null;
}

export interface ReplyInput {
	body: string;
	/** Treść sformatowana; obrazki jako `cid:hd-upload-<id>` z plików w `attachmentIds`. */
	bodyHtml?: string;
	to: string[];
	cc: string[];
	replyToArticleId?: number;
	attachmentIds: number[];
	standardAttachmentIds: number[];
	close?: boolean;
	sendClosedNotice?: boolean;
	/** Zgłoszenie ze stanowiska: okienko z odpowiedzią na tym komputerze (domyślnie tak). */
	stationPopup?: boolean;
}

export interface NoteInput {
	body: string;
	bodyHtml?: string;
	/** Zrzuty wklejone do treści (`cid:hd-upload-<id>`). */
	attachmentIds?: number[];
	/** Zakończ zgłoszenie razem z notatką (jedna transakcja); treść notatki nie trafia do zgłaszającego. */
	close?: boolean;
	sendClosedNotice?: boolean;
	/** Treść notatki to polecenie dla agenta diagnozy AI; 409, gdy diagnoza wyłączona (notatka nie zapisana). */
	diagnose?: boolean;
}

// ── Klucze zapytań ───────────────────────────────────────────────────────────

/** Prefiks wszystkich zapytań helpdesku — mutacje unieważniają go w całości. */
export const HELPDESK_QUERY_KEY = ["helpdesk"] as const;

/** Klucz z organizacją: super_admin przepina tenanta, a dane są per schemat. */
export function useHelpdeskKey() {
	const tenant = useContext(TenantSwitcherContext)?.selectedTenantId ?? null;
	return (...parts: unknown[]) => [...HELPDESK_QUERY_KEY, tenant, ...parts];
}

export const MAILBOX_OFF_HINT = "Skrzynka wyłączona — mail czekałby w kolejce do jej włączenia";

/**
 * Czy skrzynka wysyła pocztę (`undefined`, dopóki status się nie wczytał). Mail
 * „zakończone” przy wyłączonej skrzynce czekałby w kolejce i wyszedł po jej włączeniu,
 * więc okna zamknięcia domyślnie go pomijają, gdy wysyłka nie jest potwierdzona.
 */
export function useMailboxEnabled(active: boolean): boolean | undefined {
	const key = useHelpdeskKey();
	const { data } = useQuery({
		queryKey: key("mailbox-status"),
		queryFn: helpdeskApi.mailboxStatus,
		enabled: active,
		staleTime: 60_000,
	});
	return data?.enabled;
}

/**
 * Mutacja helpdesku: po sukcesie unieważnia wszystkie zapytania modułu (skrzynka,
 * liczniki, oś czasu i panel zmieniają się razem), błąd pokazuje toastem.
 * `onSuccess` biegnie przed unieważnieniem — także gdy komponent zaraz zniknie
 * (np. baner spamu po przywróceniu), czego nie zapewnia callback z `mutate()`.
 */
export function useHelpdeskMutation<TVars, TData = unknown>(
	fn: (vars: TVars) => Promise<TData>,
	opts: {
		success?: string | ((data: TData) => string);
		error?: string;
		onSuccess?: (data: TData) => void;
	} = {},
) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: fn,
		onSuccess: (data) => {
			if (opts.success)
				toast.success(typeof opts.success === "function" ? opts.success(data) : opts.success);
			opts.onSuccess?.(data);
		},
		onError: (e) =>
			toast.error(opts.error ?? "Operacja nie powiodła się", {
				description: e instanceof Error ? e.message : undefined,
			}),
		onSettled: () => queryClient.invalidateQueries({ queryKey: HELPDESK_QUERY_KEY }),
	});
}

/** Co tyle sprawdzamy, czy przybyła wiadomość — nowy mail widać w skrzynce i czacie w kilka sekund. */
export const LIVE_REFRESH_MS = 5000;

const NO_VIEWERS: Viewer[] = [];

/**
 * Odpytuje lekki znacznik `/helpdesk/version` i po jego zmianie odświeża skrzynkę
 * oraz czat; ciężkie listy nie są odpytywane co kilka sekund. Ta sama sonda zgłasza
 * otwarte zgłoszenie (`viewing`) i zwraca innych pracowników przy zgłoszeniach.
 */
export function useHelpdeskLiveRefresh(viewing?: { ticket: number; writing: boolean }) {
	const key = useHelpdeskKey();
	const queryClient = useQueryClient();
	const sound = useNewTicketSound();
	const { data, isPlaceholderData } = useQuery({
		// Zmiana zgłoszenia albo „pisze” to nowy klucz — inni widzą ją od razu, nie po 5 s.
		queryKey: key("version", viewing?.ticket ?? null, viewing?.writing ?? false),
		queryFn: () => helpdeskApi.version(viewing),
		refetchInterval: LIVE_REFRESH_MS,
		// Bez cache starych kluczy: powrót do „nie pisze” to też sonda od razu (inaczej
		// świeży wpis z cache czekał na interwał). Placeholder bierze dane poprzedniego klucza.
		gcTime: 0,
		// Pisanie w karcie w tle (albo pod oknem RustDeska) dalej odnawia obecność, a dzwonek
		// ma zadzwonić także przy schowanej karcie.
		refetchIntervalInBackground: !!viewing || sound,
		placeholderData: keepPreviousData,
	});
	const live = !isPlaceholderData && data;
	const newest = live ? data.newestTicket : undefined;
	// `?? 0`: frontend bywa wdrożony przed backendem — wtedy dzwoni przynajmniej nowe zgłoszenie.
	const reply = live ? (data.newestReply ?? 0) : undefined;
	// Próg osobno dla organizacji: jej przełączenie nie dzwoni ani nie wycisza nowych zgłoszeń.
	const scope = JSON.stringify(key());
	const newestSeen = useRef<{ scope: string; ticket: number; reply: number } | undefined>(
		undefined,
	);
	useEffect(() => {
		if (newest === undefined || reply === undefined) return;
		const prev = newestSeen.current?.scope === scope ? newestSeen.current : undefined;
		// Pierwsza sonda tylko zapamiętuje stan — bez dzwonka po otwarciu strony. Nowe zgłoszenie
		// i odpowiedź apteki w tej samej sondzie to jeden dźwięk.
		if (sound && prev && (newest > prev.ticket || reply > prev.reply)) playNewTicketSound();
		newestSeen.current = {
			scope,
			ticket: Math.max(newest, prev?.ticket ?? 0),
			reply: Math.max(reply, prev?.reply ?? 0),
		};
	}, [newest, reply, sound, scope]);
	const seen = useRef<number | undefined>(undefined);
	const version = data?.version;
	useEffect(() => {
		if (version === undefined) return;
		if (seen.current !== undefined && seen.current !== version) {
			void queryClient.invalidateQueries({ queryKey: key("inbox") });
			void queryClient.invalidateQueries({ queryKey: key("conversation") });
		}
		seen.current = version;
	}, [version, queryClient, key]);
	return data?.presence ?? NO_VIEWERS;
}

// ── Wywołania ────────────────────────────────────────────────────────────────

const json = (method: string, body?: unknown): RequestInit => ({
	method,
	body: body === undefined ? undefined : JSON.stringify(body),
});
const enc = encodeURIComponent;

/**
 * `columnFilters` (generyczny JSON `[{id, value}]` jak w raportach) to dopisek
 * do kontraktu: filtry nagłówków tabeli muszą ciąć całą skrzynkę, nie doczytaną stronę.
 */
export function inboxParams(
	f: InboxFilters,
	cursor?: string | null,
	columnFilters?: string,
): string {
	const p = new URLSearchParams({ status: f.status, alerts: String(f.alerts) });
	if (f.category.length) p.set("category", f.category.join(","));
	if (f.unpaired) p.set("unpaired", "true");
	if (f.spam) p.set("spam", "true");
	if (f.idfirm) p.set("idfirm", String(f.idfirm));
	if (f.q) p.set("q", f.q);
	if (columnFilters) p.set("columnFilters", columnFilters);
	p.set("limit", "50");
	if (cursor) p.set("cursor", cursor);
	return p.toString();
}

export const helpdeskApi = {
	inbox: (f: InboxFilters, cursor?: string | null, columnFilters?: string) =>
		apiFetch<InboxResponse>(`/helpdesk/inbox?${inboxParams(f, cursor, columnFilters)}`),
	conversation: (by: { idfirm?: number; email?: string; ticket?: number }) =>
		apiFetch<Conversation>(
			by.idfirm
				? `/helpdesk/conversations?idfirm=${by.idfirm}`
				: by.email
					? `/helpdesk/conversations?email=${enc(by.email)}`
					: `/helpdesk/conversations?ticket=${by.ticket}`,
		),
	ticket: (n: number) => apiFetch<TicketDetail>(`/helpdesk/tickets/${n}`),
	/** Zgłoszenie przyjęte telefonicznie; `warnings`: potwierdzenie nie poszło (szablon wyłączony, limit). */
	createTicket: (input: {
		idfirm: number;
		subject: string;
		body: string;
		notifyEmail: string | null;
		sendReceived: boolean;
	}) => apiFetch<{ ticket: Ticket; warnings: string[] }>("/helpdesk/tickets", json("POST", input)),
	patchTicket: (
		n: number,
		body: Partial<{
			category: Category;
			ticketType: TicketType;
			idfirm: number;
			subject: string;
			/** Nowy zgłaszający; `contactName` bez wartości czyści nazwę. */
			contactEmail: string;
			contactName: string;
		}>,
	) => apiFetch<Ticket>(`/helpdesk/tickets/${n}`, json("PATCH", body)),
	/** `warnings`: Robaczek zamknięty notatką — błędy GitHuba (zgłoszenie i tak zamknięte). */
	addNote: (n: number, note: NoteInput) =>
		apiFetch<Article & { warnings?: string[] }>(`/helpdesk/tickets/${n}/notes`, json("POST", note)),
	/** `warnings`: Robaczek zamknięty odpowiedzią — błędy GitHuba (zgłoszenie i tak zamknięte). */
	reply: (n: number, input: ReplyInput) =>
		apiFetch<Article & { warnings?: string[] }>(
			`/helpdesk/tickets/${n}/replies`,
			json("POST", input),
		),
	upload: (file: File) => {
		const form = new FormData();
		form.append("file", file);
		return apiFetch<Upload>("/helpdesk/uploads", { method: "POST", body: form });
	},
	/** `warnings`: np. issue GitHub nie zamknięte — zgłoszenie i tak jest zakończone. */
	close: (n: number, input: { sendNotice: boolean; resolutionNotes?: string }) =>
		apiFetch<{ ticket: Ticket; warnings: string[] }>(
			`/helpdesk/tickets/${n}/close`,
			json("POST", input),
		),
	reopen: (n: number) => apiFetch<Ticket>(`/helpdesk/tickets/${n}/reopen`, json("POST")),
	/** Do kwarantanny; z `rule` — także blokada przyszłych maili. */
	reportSpam: (n: number, rule?: { kind: BlockRuleKind; value: string; reason: string }) =>
		apiFetch<{ ticket: Ticket; rule: BlockRule | null }>(
			`/helpdesk/tickets/${n}/spam`,
			json("POST", { rule }),
		),
	/** `rule` — reguła, która złapała zgłoszenie (panel proponuje jej usunięcie). */
	notSpam: (n: number) =>
		apiFetch<{ ticket: Ticket; rule: BlockRule | null }>(
			`/helpdesk/tickets/${n}/not-spam`,
			json("POST"),
		),
	blockRules: () => apiFetch<BlockRule[]>("/helpdesk/block-rules"),
	createBlockRule: (rule: {
		kind: BlockRuleKind;
		value: string;
		reason: string;
		action: BlockRuleAction;
	}) => apiFetch<BlockRule>("/helpdesk/block-rules", json("POST", rule)),
	blockRulePreview: (kind: BlockRuleKind, value: string) =>
		apiFetch<BlockRulePreview>(`/helpdesk/block-rules/preview?kind=${kind}&value=${enc(value)}`),
	setBlockRule: (id: number, patch: { enabled?: boolean; action?: BlockRuleAction }) =>
		apiFetch<BlockRule>(`/helpdesk/block-rules/${id}`, json("PATCH", patch)),
	/** Reguła „tylko w historii apteki” dla dotychczasowych zgłoszeń (też z OTRS). */
	applyBlockRule: (id: number) =>
		apiFetch<{ applied: number }>(`/helpdesk/block-rules/${id}/apply`, json("POST")),
	deleteBlockRule: (id: number) => apiFetch<void>(`/helpdesk/block-rules/${id}`, json("DELETE")),

	createIncident: (title: string, ticketNumbers: number[]) =>
		apiFetch<Incident>("/helpdesk/incidents", json("POST", { title, ticketNumbers })),
	incident: (id: number) =>
		apiFetch<{ incident: Incident; tickets: IncidentTicket[]; candidates: IncidentCandidate[] }>(
			`/helpdesk/incidents/${id}`,
		),
	incidentSuggestion: () => apiFetch<IncidentSuggestion | null>("/helpdesk/incidents/suggestion"),
	/** Każda trwająca awaria: liczniki zgłoszeń i nowe pasujące (pasek w skrzynce, zakładka w czacie). */
	incidentMatches: () =>
		apiFetch<
			{
				incident: Incident;
				tickets: number;
				openTickets: number;
				candidates: IncidentCandidate[];
			}[]
		>("/helpdesk/incidents/matches"),
	/** Dołącza zgłoszenia i wysyła im ostatnią odpowiedź awarii (gdy była). */
	/** `close`: zakończ bez maila; apteka dostanie tylko komunikat o zakończeniu awarii. */
	joinIncident: (id: number, ticketNumbers: number[], close = false) =>
		apiFetch<{ joined: number; sent: number; closed: number }>(
			`/helpdesk/incidents/${id}/join`,
			json("POST", { ticketNumbers, close }),
		),
	detachFromIncident: (id: number, number: number) =>
		apiFetch<void>(`/helpdesk/incidents/${id}/tickets/${number}`, json("DELETE")),
	/** Ten sam mail osobno w każdym otwartym zgłoszeniu awarii. */
	replyToIncident: (id: number, message: { body: string; bodyHtml: string }) =>
		apiFetch<{ sent: number }>(`/helpdesk/incidents/${id}/reply`, json("POST", message)),
	/** Wiadomość końcowa w każdym zgłoszeniu i zamknięcie wszystkich. */
	resolveIncident: (id: number, message: { body: string; bodyHtml: string }) =>
		apiFetch<{ incident: Incident; sent: number; closed: number }>(
			`/helpdesk/incidents/${id}/resolve`,
			json("POST", message),
		),
	attachmentBlob: (id: number) => apiFetchBlob(`/helpdesk/attachments/${id}`),
	/** Obrazki `cid:` zgłoszenia naraz: id → sha256 → `data:` URL (ten sam plik raz). */
	inlineImages: (ids: number[]) =>
		apiFetch<{ ids: Record<string, string>; files: Record<string, string> }>(
			`/helpdesk/attachments/inline?ids=${ids.join(",")}`,
		),
	/** Uproszczony podgląd ODT/DOCX: sam tekst; plik nie do pokazania = 422 z komunikatem. */
	attachmentPreview: (id: number) => apiFetch<DocPreview>(`/helpdesk/attachments/${id}/preview`),

	pharmacies: (q: string) => apiFetch<PharmacyName[]>(`/helpdesk/pharmacies?q=${enc(q)}`),
	panel: (idfirm: number) => apiFetch<PharmacyPanel>(`/helpdesk/pharmacies/${idfirm}/panel`),

	pairing: () => apiFetch<{ items: PairingItem[] }>("/helpdesk/pairing"),
	pair: (email: string, idfirms: number[]) =>
		apiFetch<void>("/helpdesk/pairing", json("POST", { email, idfirms })),
	unpair: (email: string, idfirm: number) =>
		apiFetch<void>(`/helpdesk/pairing/${enc(email)}/${idfirm}`, json("DELETE")),
	/** „To nie ta apteka”: zdejmuje z kontaktu tylko podpowiedź tej apteki. */
	removeHint: (email: string, idfirm: number) =>
		apiFetch<void>(`/helpdesk/contacts/${enc(email)}/hints/${idfirm}`, json("DELETE")),
	/** `null` zdejmuje etykietę — adres wraca do parowania. */
	setContactLabel: (email: string, label: string | null) =>
		apiFetch<{ email: string; label: string | null }>(
			`/helpdesk/contacts/${enc(email)}/label`,
			json("POST", { label }),
		),
	contactLabels: () => apiFetch<{ label: string; count: number }[]>("/helpdesk/contact-labels"),
	/** Ręczne parowanie adresu z urządzeniem biurowym (`null` odpina; etykieta zostaje). */
	setContactDevice: (email: string, deviceUuid: string | null) =>
		apiFetch<void>(`/helpdesk/contacts/${enc(email)}/device`, json("POST", { deviceUuid })),
	officeDevices: () => apiFetch<PanelDevice[]>("/helpdesk/office-devices"),
	contacts: (f: { idfirm?: number; q?: string; addressBook?: boolean }) => {
		const p = new URLSearchParams();
		if (f.idfirm) p.set("idfirm", String(f.idfirm));
		if (f.q) p.set("q", f.q);
		if (f.addressBook) p.set("addressBook", "true");
		return apiFetch<Contact[]>(`/helpdesk/contacts?${p}`);
	},
	/** Pole pominięte (`undefined`) zostaje bez zmian; pusty tekst je czyści. */
	saveContact: (email: string, body: { name?: string; phone?: string; addressBook?: boolean }) =>
		apiFetch<void>(`/helpdesk/contacts/${enc(email)}`, json("PUT", body)),

	templates: () => apiFetch<Template[]>("/helpdesk/templates"),
	saveTemplate: (key: TemplateKey, body: { subject: string; body: string; enabled: boolean }) =>
		apiFetch<void>(`/helpdesk/templates/${key}`, json("PUT", body)),
	previewTemplate: (body: { key: TemplateKey; subject: string; body: string; number?: number }) =>
		apiFetch<{ subject: string; body: string; html?: string | null }>(
			"/helpdesk/templates/preview",
			json("POST", body),
		),

	standardAttachments: () => apiFetch<StandardAttachment[]>("/helpdesk/standard-attachments"),
	addStandardAttachment: (file: File, name: string) => {
		const form = new FormData();
		form.append("file", file);
		form.append("name", name);
		return apiFetch<StandardAttachment>("/helpdesk/standard-attachments", {
			method: "POST",
			body: form,
		});
	},
	deleteStandardAttachment: (id: number) =>
		apiFetch<void>(`/helpdesk/standard-attachments/${id}`, json("DELETE")),

	settings: <T>(section: SettingsSection) => apiFetch<T>(`/helpdesk/settings/${section}`),
	saveSettings: (section: SettingsSection, body: unknown) =>
		apiFetch<void>(`/helpdesk/settings/${section}`, json("PUT", body)),
	/** Wypisanie się zalogowanego pracownika z jego raportu dziennego. */
	reportSubscription: () => apiFetch<{ subscribed: boolean }>("/helpdesk/report/subscription"),
	setReportSubscription: (subscribed: boolean) =>
		apiFetch<{ subscribed: boolean }>("/helpdesk/report/subscription", json("PUT", { subscribed })),
	assignees: () => apiFetch<Assignee[]>("/helpdesk/assignees"),
	/** `null` zdejmuje przypisanie. */
	assign: (n: number, userId: number | null) =>
		apiFetch<Ticket>(`/helpdesk/tickets/${n}/assignee`, json("PUT", { userId })),
	/** Przypomnienie mailem obecnie przypisanej osobie — wysyłka od razu. */
	remindAssignee: (n: number) =>
		apiFetch<Ticket>(`/helpdesk/tickets/${n}/assignee/remind`, json("POST", {})),
	/** Dziennik prób alertu SMS; `smsReady` false = SMSAPI nieskonfigurowane, nic nie wyjdzie. */
	alertLog: () => apiFetch<{ smsReady: boolean; items: AlertLogItem[] }>("/helpdesk/alerts"),
	testAlert: () => apiFetch<AlertLogItem>("/helpdesk/settings/alerts/test", json("POST")),
	testMailbox: () => apiFetch<MailboxTest>("/helpdesk/settings/mailbox/test", json("POST")),
	testAi: () =>
		apiFetch<{ ok: boolean; model: string; durationMs: number; error?: string }>(
			"/helpdesk/settings/ai/test",
			json("POST"),
		),
	mailboxStatus: () => apiFetch<MailboxStatus>("/helpdesk/mailbox/status"),
	mailboxOutbox: () =>
		apiFetch<{ total: number; items: PendingMail[] }>("/helpdesk/mailbox/outbox"),
	/** Wiadomości `failed` (najnowsze 200, także Robaczka) — pasek w skrzynce i lista w Ustawieniach. */
	mailboxFailed: () =>
		apiFetch<{ total: number; items: PendingMail[] }>("/helpdesk/mailbox/outbox?failed=true"),
	/** `failed` z powrotem do kolejki, nowy cykl prób; 409, gdy już nie czeka. */
	resendArticle: (id: number) => apiFetch<Article>(`/helpdesk/articles/${id}/resend`, json("POST")),
	/** `hidden`: karta w tle — obecność tak, ale bez oznaczania odpowiedzi apteki jako przeczytanej. */
	version: (viewing?: { ticket: number; writing: boolean }) =>
		apiFetch<{ version: number; newestTicket: number; newestReply: number; presence: Viewer[] }>(
			`/helpdesk/version${viewing ? `?ticket=${viewing.ticket}&writing=${viewing.writing}${document.visibilityState === "hidden" ? "&hidden=true" : ""}` : ""}`,
		),
	online: (idle: number, hidden: boolean) =>
		apiFetch<OnlineAdmin[]>(`/helpdesk/online?idle=${idle}&hidden=${hidden}`),

	runAi: (n: number, kind: "classify" | "draft" | "diagnose" | "summary") =>
		apiFetch<{ queued: true }>(`/helpdesk/tickets/${n}/ai/${kind}`, json("POST")),
	/** „Przekaż do diagnozy”: istniejąca notatka IT jako polecenie dla agenta; 409 przy trwającej diagnozie. */
	orderDiagnosis: (n: number, articleId: number) =>
		apiFetch<{ queued: true }>(`/helpdesk/tickets/${n}/notes/${articleId}/diagnose`, json("POST")),
	aiStatus: (n: number) => apiFetch<AiStatus>(`/helpdesk/tickets/${n}/ai`),
	historySummary: (idfirm: number) =>
		apiFetch<{ summary: string; ticketCount: number; model: string; durationMs: number }>(
			"/helpdesk/conversations/history-summary",
			json("POST", { idfirm }),
		),
	/** „Popraw z AI”: HTML okna ze znacznikami `[[obrazek-N]]`; 409 przy wyłączonym AI. */
	rewrite: (style: RewriteStyle, html: string) =>
		apiFetch<{ html: string }>("/helpdesk/ai/rewrite", json("POST", { style, html })),

	search: (p: URLSearchParams, cursor?: string | null) =>
		apiFetch<{ items: SearchHit[]; semanticAvailable: boolean; nextCursor?: string | null }>(
			`/helpdesk/search?${p}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
		),

	runs: () => apiFetch<Run[]>("/helpdesk/runs"),
	startRun: (kind: RunKind, params: Record<string, unknown> = {}) =>
		apiFetch<Run>("/helpdesk/runs", json("POST", { kind, params })),
	cancelRun: (id: number) => apiFetch<void>(`/helpdesk/runs/${id}/cancel`, json("POST")),
	/** „Wznów” zadania z błędem — od miejsca przerwania; 409, gdy ten rodzaj już trwa. */
	resumeRun: (id: number) => apiFetch<Run>(`/helpdesk/runs/${id}/resume`, json("POST")),
};

// ── Pomocnicze ───────────────────────────────────────────────────────────────

/**
 * Wiek jak w OTRS: minuty od wpłynięcia do teraz, a przy zakończonym — do zakończenia.
 * Otwarte ponownie zachowuje dawne `closedAt`, więc decyduje status.
 */
export function ageMinutes(
	r: Pick<InboxItem, "openedAt" | "closedAt" | "status">,
	now: number = Date.now(),
): number {
	const end = r.status === "closed" && r.closedAt ? new Date(r.closedAt).getTime() : now;
	return Math.max(0, Math.floor((end - new Date(r.openedAt).getTime()) / 60_000));
}

/** Minuty od wpłynięcia do pierwszego działania pracownika; `null` = nikt jeszcze nie zareagował. */
export function reactionMinutes(r: Pick<InboxItem, "openedAt" | "firstTouchAt">): number | null {
	if (!r.firstTouchAt) return null;
	const ms = new Date(r.firstTouchAt).getTime() - new Date(r.openedAt).getTime();
	return Math.max(0, Math.floor(ms / 60_000));
}

/** „<1 min”, „45 min”, „2 h 05 min”, „3 dni 4 h”. */
export function formatMinutes(minutes: number): string {
	if (minutes < 1) return "<1 min";
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} h ${String(minutes % 60).padStart(2, "0")} min`;
	const days = Math.floor(hours / 24);
	return `${days} ${days === 1 ? "dzień" : "dni"} ${hours % 24} h`;
}

export function formatDateTime(value: string | null | undefined): string {
	if (!value) return "—";
	return new Date(value).toLocaleString("pl-PL", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatDate(value: string | null | undefined): string {
	if (!value) return "—";
	return new Date(value).toLocaleDateString("pl-PL");
}

export function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

/** Pobranie załącznika przez `apiFetch` (token), zapis pod nazwą z bazy. */
export async function downloadAttachment(att: { id: number; filename: string }): Promise<void> {
	const { blob } = await helpdeskApi.attachmentBlob(att.id);
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = att.filename;
	a.click();
	setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Adres czatu: apteka albo (niesparowane) adres nadawcy. */
export function chatSearch(target: {
	idfirm?: number | null;
	email?: string | null;
	ticket?: number;
	article?: number | null;
}) {
	return {
		idfirm: target.idfirm ?? undefined,
		email: target.idfirm ? undefined : (target.email ?? undefined),
		ticket: target.ticket,
		article: target.article ?? undefined,
	};
}
